/// <amd-module name="Test/TestPlansLibrary/TestPlansWorkItemPage/Component/WorkItemPageComponent" />
import * as React from "react";
import * as Resources from "Test/TestPlansLibrary/LibraryHub/Resources";
import { TestPlansLibraryConstants, WorkItemConstants } from "Test/TestPlansLibrary/LibraryHub/Constants";
import { IWorkItemPageOptions } from "Test/TestPlansLibrary/LibraryHub/Redux/TestPlansLibraryContracts";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { VssComponent, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { CustomHeader, HeaderTitle, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem, HeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";

export interface IWorkItemPageProps extends IVssComponentProperties {}
/**
 * WorkItemPageComponent: The Component which renders full page view for creating/editing workitem
 */

export class WorkItemPageComponent extends VssComponent<IWorkItemPageProps> {
    private _pageContext: IVssPageContext;
    private _showWITForm: ObservableValue<boolean>;
    private _workItemPageOptions: IWorkItemPageOptions;

    constructor(props: IWorkItemPageProps, context: IVssComponentContext) {
        super(props, context);
        this._pageContext = context.pageContext;
        this._showWITForm = new ObservableValue<boolean>(false);
        this._setWorkItemPageOptions();
    }

    public render(): JSX.Element {
        return (
            <Page className="testplans-workitems-page">
                <div className="testplans-workitems-page-container">
                    <CustomHeader className="testplans-workitems-page-header">
                        <HeaderTitle
                            titleSize={TitleSize.Medium}
                            className="bolt-header-title-area scroll-hidden bolt-header-title body-xl m header-title"
                        >
                            {this._workItemPageOptions.headerTitle}
                        </HeaderTitle>
                        <HeaderCommandBar items={this._getCommandBarItems()} />
                    </CustomHeader>
                    <div id="testplans-workitem-form-container"></div>
                    <Observer showPanel={this._showWITForm}>
                        {(props: { showPanel: boolean }) => {
                            return (
                                props.showPanel && (
                                    <LegacyComponent
                                        modules={["TestManagement/Scripts/Shared/WorkItemPageProxy"]}
                                        wrappedType="workitem-page-proxy"
                                        wrappedProps={{
                                            pageContext: this._pageContext,
                                            container: document.getElementById("testplans-workitem-form-container"),
                                            workItemType: this._workItemPageOptions.workItemType,
                                            workItemId: this._workItemPageOptions.workItemId
                                        }}
                                    />
                                )
                            );
                        }}
                    </Observer>
                </div>
            </Page>
        );
    }

    public componentDidMount() {
        this._showWITForm.value = true;
    }

    private _setWorkItemPageOptions() {
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const routeValues = navigationService.getCurrentRoute().routeValues;
        if (routeValues["type"] === TestPlansLibraryConstants.TestCaseTabId && routeValues["mode"] === WorkItemConstants.WorkItemCreateMode) {
            this._workItemPageOptions = {
                navigationButtonTitle: Resources.BackToTestCases,
                headerTitle: Resources.NewTestCase,
                workItemType: TestPlansLibraryConstants.TestCaseWorkItemType,
                routeId: "ms.vss-test-web.testplans-library-route"
            };
        } else if (routeValues["type"] === TestPlansLibraryConstants.TestCaseTabId && routeValues["mode"] === WorkItemConstants.WorkItemEditMode) {
            this._workItemPageOptions = {
                navigationButtonTitle: Resources.BackToTestCases,
                headerTitle: Resources.TestCase + routeValues["id"],
                workItemType: undefined,
                routeId: "ms.vss-test-web.testplans-library-route",
                workItemId: routeValues["id"]
            };
        }
    }

    private _navigateBack() {
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let projectName: string = "";

        if (tfsPageData && tfsPageData.project) {
            projectName = tfsPageData.project.name;
        }

        const libraryUrl: string = routeUrl(this._pageContext, this._workItemPageOptions.routeId, {
            project: projectName
        });
        FastPageSwitch(this.context.pageContext, libraryUrl, true);
    }

    private _getCommandBarItems(): IHeaderCommandBarItem[] {
        const tabBarCommands: IHeaderCommandBarItem[] = [];
        tabBarCommands.push({
            id: TestPlansLibraryConstants.BackButton,
            iconProps: {
                iconName: "RevToggleKey",
                className: "header-command-bar-icons"
            },
            onActivate: () => {
                this._navigateBack();
            },
            subtle: true,
            className: "header-command-bar-actions",
            text: this._workItemPageOptions.navigationButtonTitle,
            important: true
        });
        return tabBarCommands;
    }
}

VssComponent.register("workItemPageComponent", WorkItemPageComponent);
